import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { useChartAccessibility } from '../../hooks/useChartAccessibility';

export interface GaugeChartProps {
  value: number;
  max?: number;
  min?: number;
  height?: number | string;
  label?: string;
  unit?: string;
  thresholds?: {
    warning?: number;
    danger?: number;
  };
  colors?: {
    good?: string;
    warning?: string;
    danger?: string;
    background?: string;
  };
  className?: string;
}

const DEFAULT_COLORS = {
  good: '#10b981', // emerald-500
  warning: '#f59e0b', // amber-500
  danger: '#ef4444', // red-500
  background: '#e5e7eb', // gray-200
};

export function GaugeChart({
  value,
  max = 100,
  min = 0,
  height = '100%',
  label,
  unit = '%',
  thresholds = { warning: 70, danger: 50 },
  colors = DEFAULT_COLORS,
  className = '',
}: GaugeChartProps) {
  const a11y = useChartAccessibility();
  const normalizedValue = Math.min(Math.max(value, min), max);
  const percentage = ((normalizedValue - min) / (max - min)) * 100;

  // In a color-vision mode, source the good/warning/danger tones from the
  // color-blind-safe status palette so severity stays distinguishable.
  const good = a11y.colorBlindActive ? a11y.statusColors.healthy : colors.good || DEFAULT_COLORS.good;
  const warning = a11y.colorBlindActive ? a11y.statusColors.warning : colors.warning || DEFAULT_COLORS.warning;
  const danger = a11y.colorBlindActive ? a11y.statusColors.critical : colors.danger || DEFAULT_COLORS.danger;

  // Determine color based on thresholds
  let fillColor = good;
  if (thresholds.danger !== undefined && percentage < thresholds.danger) {
    fillColor = danger;
  } else if (thresholds.warning !== undefined && percentage < thresholds.warning) {
    fillColor = warning;
  }

  // Create data for the gauge (180 degree arc)
  const data = [
    { name: 'value', value: percentage },
    { name: 'empty', value: 100 - percentage },
  ];

  return (
    <div className={`relative ${className}`} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="70%"
            startAngle={180}
            endAngle={0}
            innerRadius="60%"
            outerRadius="80%"
            paddingAngle={0}
            dataKey="value"
            stroke="none"
            isAnimationActive={a11y.isAnimationActive}
            animationDuration={a11y.animationDuration}
          >
            <Cell fill={fillColor} />
            <Cell fill={colors.background || DEFAULT_COLORS.background} />
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ top: '20%' }}>
        <span className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          {normalizedValue.toFixed(0)}
          <span className="text-lg font-normal text-gray-500 dark:text-gray-400">{unit}</span>
        </span>
        {label && <span className="text-sm text-gray-500 mt-1 dark:text-gray-400">{label}</span>}
      </div>
    </div>
  );
}
